"use strict";
/**
 * Permissions — SPEC §3 (tenancy, roles & permissions), §6.4 (the privileges
 * matrix on the employee form).
 *
 * One flat, stable, dotted key per privilege. The same keys drive:
 *  - the RBAC middleware on every API route (§11),
 *  - which tabs and actions the staff app renders (§3 — the Bills tab is hidden
 *    entirely without `bills.view`),
 *  - the checkbox matrix in Settings -> Employees,
 *  - i18n label lookup (`permissions.<key>` in the en/ar/ckb translation files).
 *
 * Keys are persisted on `PermissionSet` rows, so they are an append-only
 * contract: never rename or reuse a key without a migration.
 *
 * The venue owner/admin implicitly holds every permission; employee accounts
 * hold the subset the owner ticked.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUDITED_PERMISSIONS = exports.PERMISSION_LABELS = exports.PERMISSION_KEYS = exports.PERMISSIONS_BY_AREA = exports.PERMISSION_AREAS = exports.PERMISSIONS = void 0;
exports.isPermissionKey = isPermissionKey;
exports.parsePermissionKeys = parsePermissionKeys;
exports.toPermissionSet = toPermissionSet;
exports.hasPermission = hasPermission;
exports.hasAllPermissions = hasAllPermissions;
exports.hasAnyPermission = hasAnyPermission;
exports.missingPermissions = missingPermissions;
/* -------------------------------------------------------------------------- */
/* The keys, grouped by area                                                  */
/* -------------------------------------------------------------------------- */
exports.PERMISSIONS = {
    dashboard: {
        /** §5.1 — see the Dashboard tab and its figures at all. */
        view: 'dashboard.view',
    },
    bills: {
        /**
         * §3/§5.6 — see the Bills tab AT ALL. Without it the tab is not rendered
         * and the API refuses every bill query.
         */
        view: 'bills.view',
        /**
         * Take money that writes a PAID bill: settle a tab at the till — the
         * checkout that turns open sittings and orders into PAID bills
         * (`POST /tabs/:id/checkout`, beside `sessions.manage`) — and take a
         * balance refill (`POST /customers/:id/refill`, beside
         * `customers.refill`). Required from 2026-09-24; migration
         * 20260924000000_settings_wave granted it to every holder of
         * `sessions.manage` or `customers.refill` so no cashier lost the till or
         * the refill button.
         */
        create: 'bills.create',
        /** Audited (§3). Stamps the "Receipt Editor" column. */
        edit: 'bills.edit',
        /** Audited (§3). */
        delete: 'bills.delete',
    },
    discounts: {
        /** §3 — apply a discount to a session, order or tab. Audited. */
        apply: 'discounts.apply',
    },
    refunds: {
        /**
         * §3 — give money back: refund a paid order to the wallet, whole or item
         * by item (beside `cafe.return`), and pay out a balance (beside
         * `customers.refundBalance`). Audited. Required on those paths from
         * 2026-09-24; the settings wave migration granted it to every holder of
         * `cafe.return` or `customers.refundBalance` (the keys those paths needed
         * the day before) so nothing anyone could do then stopped working. The
         * cafe till's own return — a negative line on a tab not yet settled —
         * stays on `cafe.return` alone.
         */
        process: 'refunds.process',
    },
    reports: {
        /** §5.7 — Reports tab and Overall Report. */
        view: 'reports.view',
        /** §5.7 — CSV / PDF export and printing of reports. */
        export: 'reports.export',
    },
    devices: {
        /** §3/§5.2 — add, edit, delete devices, categories and device groups. */
        manage: 'devices.manage',
    },
    sessions: {
        /** §3 — open/close sessions, open tabs, extend, transfer, pause. */
        manage: 'sessions.manage',
        /**
         * §5.3 — void a session with a reason (`POST /sessions/:id/void`,
         * `voidSessionSchema`): it ends VOID and bills nothing. Audited, separate
         * from close.
         */
        void: 'sessions.void',
    },
    cafe: {
        /** §3/§5.4 — take cafe orders and send them to the stations. */
        order: 'cafe.order',
        /**
         * Send items BACK: a negative order that credits the tab, returns the
         * stock and tells the station to un-make it.
         *
         * Separate from `order` and audited, because it is the one thing on this
         * screen that moves money the customer's way. Someone who can ring up a
         * sale and reverse it unwatched can take the cash for the difference.
         */
        return: 'cafe.return',
    },
    customers: {
        /** §3/§5.5 — customer accounts: create, edit, points, per-account discount. */
        manage: 'customers.manage',
        /** §5.5 — take a deposit and credit balance (a Refill). Touches cash. */
        refill: 'customers.refill',
        /** §5.5 — pay out remaining balance; only when "Enable Balance Refund" is on. */
        refundBalance: 'customers.refundBalance',
    },
    products: {
        /** §3/§6.3 — products, product categories, tags, stock. */
        manage: 'products.manage',
    },
    employees: {
        /** §3/§6.4 — employee accounts, credentials and their privilege matrix. */
        manage: 'employees.manage',
    },
    gameAccounts: {
        /** §3/§6.5 — venue launcher accounts. Password reveals are audited. */
        manage: 'gameAccounts.manage',
    },
    games: {
        /** §3/§6.6 — game library, covers, per-device executable paths. */
        manage: 'games.manage',
    },
    apps: {
        /** §3/§6.7 — apps offered in the portal. */
        manage: 'apps.manage',
    },
    media: {
        /** §7 — upload idle videos and push them to devices/groups. */
        manage: 'media.manage',
    },
    printers: {
        /** §6.1/§10 — printers list, routing, test print. */
        manage: 'printers.manage',
    },
    portal: {
        /** §9 — portal content: challenges, quests, prizes, featured items, feed. */
        manage: 'portal.manage',
    },
    finance: {
        /** §5.7 — Expenses / Incomes entry screen. */
        manageExpenses: 'finance.manageExpenses',
        /**
         * §3 — the cash drawer's history of closed sessions (`GET
         * /cash-drawer/sessions`, see cash-drawer.ts). Closing the drawer needs
         * only `bills.create`; the key keeps its name so no matrix changes.
         */
        manageShifts: 'finance.manageShifts',
    },
    audit: {
        /** §3 — read the audit log of sensitive mutations (`GET /audit-log`, see audit.ts). */
        view: 'audit.view',
    },
    settings: {
        /** §3/§6.1 — General Settings, incl. rounding rule and business-day boundary. */
        manage: 'settings.manage',
    },
    hotkey: {
        /**
         * §3/§6.1 — use the hot-key + password to drop a locked client PC to the
         * Windows desktop for maintenance, WITHOUT creating a reservation. Logged.
         */
        unlock: 'hotkey.unlock',
    },
    safe: {
        /** Wave 3 §12.1 — see the safe balance and the Bonds ledger. */
        view: 'safe.view',
        /**
         * Wave 3 — record withdrawals/deposits against the main safe, and the cash
         * drawer's mid-session pickups, owner takes and top-ups (§3). Audited.
         */
        manage: 'safe.manage',
    },
};
/** Ordered list of areas, for rendering the matrix. */
exports.PERMISSION_AREAS = Object.keys(exports.PERMISSIONS);
/** Keys grouped by area, in declaration order. */
exports.PERMISSIONS_BY_AREA = Object.fromEntries(Object.entries(exports.PERMISSIONS).map(([area, keys]) => [area, Object.values(keys)]));
/** Flat list of every permission key. Also what an owner/admin effectively holds. */
exports.PERMISSION_KEYS = exports.PERMISSION_AREAS.flatMap((area) => exports.PERMISSIONS_BY_AREA[area]);
/** English fallback labels; production UI resolves `permissions.<key>` via i18n (§11). */
exports.PERMISSION_LABELS = {
    'dashboard.view': 'View dashboard',
    'bills.view': 'View Bills tab',
    'bills.create': 'Create bills',
    'bills.edit': 'Edit bills',
    'bills.delete': 'Delete bills',
    'discounts.apply': 'Apply discounts',
    'refunds.process': 'Process refunds',
    'reports.view': 'View reports',
    'reports.export': 'Export and print reports',
    'devices.manage': 'Manage devices',
    'sessions.manage': 'Open and close sessions',
    'sessions.void': 'Void sessions',
    'cafe.order': 'Take cafe orders',
    'cafe.return': 'Return cafe items',
    'customers.manage': 'Manage customer accounts',
    'customers.refill': 'Take account refills',
    'customers.refundBalance': 'Refund account balance',
    'products.manage': 'Manage products and categories',
    'employees.manage': 'Manage employees',
    'gameAccounts.manage': 'Manage game accounts',
    'games.manage': 'Manage games',
    'apps.manage': 'Manage apps',
    'media.manage': 'Manage idle media',
    'printers.manage': 'Manage printers',
    'portal.manage': 'Manage portal content',
    'finance.manageExpenses': 'Manage expenses and incomes',
    'finance.manageShifts': 'Manage shifts and cash drawer',
    'audit.view': 'View audit log',
    'settings.manage': 'Manage settings',
    'hotkey.unlock': 'Use hot-key unlock',
    'safe.view': 'View safe and bonds',
    'safe.manage': 'Record safe withdrawals and deposits',
};
/**
 * Permissions whose use must write an audit log entry (§3). Enforced by the
 * audit interceptor, listed here so server and UI agree.
 */
exports.AUDITED_PERMISSIONS = [
    'bills.edit',
    'bills.delete',
    'cafe.return',
    'discounts.apply',
    'refunds.process',
    'sessions.void',
    'customers.refundBalance',
    'gameAccounts.manage',
    'products.manage',
    'settings.manage',
    'hotkey.unlock',
    'safe.manage',
];
function isPermissionKey(value) {
    return typeof value === 'string' && exports.PERMISSION_KEYS.includes(value);
}
/**
 * Filter unknown input (a JSON column, an API body) down to keys that still
 * exist — tolerant of permissions removed in a later release.
 */
function parsePermissionKeys(values) {
    return values.filter(isPermissionKey);
}
/** Normalise to a Set for repeated checks (route guards, render loops). */
function toPermissionSet(granted) {
    return granted instanceof Set ? granted : new Set(granted);
}
/**
 * The single check. Owner/admin accounts are expected to be granted
 * {@link PERMISSION_KEYS} in full rather than special-cased here, so the same
 * function answers for every caller.
 *
 * ```ts
 * if (!hasPermission(employee.permissions, PERMISSIONS.bills.view)) return null; // hide the tab
 * ```
 */
function hasPermission(granted, required) {
    return granted instanceof Set
        ? granted.has(required)
        : granted.includes(required);
}
/** True only if every required permission is held (AND). */
function hasAllPermissions(granted, required) {
    const set = toPermissionSet(granted);
    return required.every((key) => set.has(key));
}
/** True if at least one required permission is held (OR). */
function hasAnyPermission(granted, required) {
    const set = toPermissionSet(granted);
    return required.some((key) => set.has(key));
}
/** Which of `required` are missing — for 403 bodies and disabled-button tooltips. */
function missingPermissions(granted, required) {
    const set = toPermissionSet(granted);
    return required.filter((key) => !set.has(key));
}
//# sourceMappingURL=permissions.js.map